import { supabase } from "@/integrations/supabase/client";

interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  limit: number;
  retryAfter: number | null;
}

/**
 * Check rate limit for a specific endpoint
 * Returns whether the request is allowed and remaining quota
 */
export const checkRateLimit = async (endpoint: string): Promise<RateLimitResult> => {
  try {
    const { data, error } = await supabase.functions.invoke("rate-limit", {
      body: { endpoint },
    });

    if (error) {
      console.warn("Rate limit check failed:", error);
      // On error, allow the request
      return { allowed: true, remaining: 100, limit: 100, retryAfter: null };
    }

    return {
      allowed: data.allowed,
      remaining: data.remaining || 0,
      limit: data.limit || 100,
      retryAfter: data.retry_after || null,
    };
  } catch (err) {
    console.warn("Rate limit check error:", err);
    // On error, allow the request
    return { allowed: true, remaining: 100, limit: 100, retryAfter: null };
  }
};

/**
 * Endpoint types for rate limiting
 */
export const RateLimitEndpoints = {
  // Listing endpoints
  PROFILES_LIST: "profiles_list",
  PROFILES_VIEW: "profiles_view",
  CHALLENGES_LIST: "challenges_list",
  COMMUNITY_LIST: "community_list",
  SEARCH: "search",
  // Form submission endpoints
  FORM_CONTACT: "form_contact",
  FORM_COMMUNITY_POST: "form_community_post",
  FORM_CHALLENGE: "form_challenge",
  FORM_TESTIMONIAL: "form_testimonial",
  FORM_COMMENT: "form_comment",
} as const;

/**
 * Hook for form rate limiting with user feedback
 */
export const useFormRateLimit = () => {
  const checkFormRateLimit = async (
    endpoint: string
  ): Promise<{ allowed: boolean; message?: string }> => {
    const result = await checkRateLimit(endpoint);
    
    if (!result.allowed) {
      const minutes = Math.ceil((result.retryAfter || 300) / 60);
      return {
        allowed: false,
        message: `You're submitting too quickly. Please wait ${minutes} minute${minutes > 1 ? 's' : ''} before trying again.`,
      };
    }
    
    return { allowed: true };
  };

  return { checkFormRateLimit };
};
